import { useState, useEffect, useCallback } from 'react';

// Is the app already running as an installed PWA (standalone window / iOS
// home-screen launch)? If so there's nothing to install.
function detectStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    window.navigator.standalone === true
  );
}

// iOS Safari never fires `beforeinstallprompt` — installing there is the manual
// Share → "Add to Home Screen" flow, so we detect it to show instructions
// instead of a one-tap button.
function detectIOS() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
}

/**
 * PWA install state. Captures the browser's deferred `beforeinstallprompt`
 * event so we can fire the native install dialog on demand (Chrome/Edge/
 * Android), and reports whether we're on iOS (manual install) or already
 * installed.
 *
 * Returns:
 *   installed    — running as an installed app already
 *   isIOS        — needs the manual Share → Add to Home Screen flow
 *   canPrompt    — a native install dialog is available right now
 *   installable  — either canPrompt or iOS, and not already installed
 *   promptInstall() — fires the native dialog; resolves 'accepted'|'dismissed'|null
 */
export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(detectStandalone);
  const isIOS = detectIOS();

  useEffect(() => {
    const onBeforeInstall = (e) => {
      // Stop Chrome's mini-infobar so we can surface install on our own terms.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    let outcome = null;
    try {
      ({ outcome } = await deferredPrompt.userChoice);
    } catch {
      outcome = null;
    }
    // The event can only be used once.
    setDeferredPrompt(null);
    return outcome;
  }, [deferredPrompt]);

  const canPrompt = !!deferredPrompt;
  return {
    installed,
    isIOS,
    canPrompt,
    installable: !installed && (canPrompt || isIOS),
    promptInstall,
  };
}
